import dayjs from 'dayjs';

/**
 * MR price prefill (2026-09-29): at submit the server fills coded lines with the price we last
 * paid (a PO) or, for items never bought, the catalog list price. These fields come back on MR
 * lines for procurement, approvers and admins only; requesters never receive them.
 */
export interface PrefillInfo {
  prefillPrice?: number | null;
  prefillSource?: 'po' | 'list' | null;
  prefillRef?: string | null;
  prefillVendorName?: string | null;
  prefillDate?: string | null;
  requesterEstPrice?: number | null;
  catalogPrice?: number | null;
  priceHistory?: {
    count: number;
    min: number;
    max: number;
    latest: { price: number; poId: string; date: string | null; vendorName: string | null };
  } | null;
}

const money = (n: number) => Number(n).toLocaleString('en-US', { maximumFractionDigits: 2 });

/** True while the line still carries the price the submit step filled in. */
export function onPrefill(l: PrefillInfo & { unitPrice?: number | string | null }): boolean {
  if (!l.prefillSource || l.prefillPrice == null || l.unitPrice == null) return false;
  return Math.abs(Number(l.unitPrice) - Number(l.prefillPrice)) < 0.005;
}

/** "last paid · PO-000544 · Salem · 31 Aug" or "list price · Salem · 6 Sep". */
export function prefillLabel(l: PrefillInfo): string {
  const parts = [
    l.prefillSource === 'po' ? 'last paid' : 'list price',
    l.prefillSource === 'po' ? l.prefillRef : null,
    l.prefillVendorName,
    l.prefillDate ? dayjs(l.prefillDate).format('D MMM') : null,
  ].filter(Boolean);
  return parts.join(' · ');
}

/** "bought 3 times, always 1,440" / "bought 3 times, 2,300 – 2,601"; null when bought once or never. */
export function historyLabel(l: PrefillInfo): string | null {
  const h = l.priceHistory;
  if (!h || h.count < 2) return null;
  return h.max - h.min < 0.005
    ? `bought ${h.count} times, always ${money(h.min)}`
    : `bought ${h.count} times, ${money(h.min)} – ${money(h.max)}`;
}

/** A purchase made after the price was filled in, while the line is not ordered yet. */
export function newerPurchase(l: PrefillInfo, ordered: boolean) {
  const h = l.priceHistory;
  if (ordered || l.prefillSource !== 'po' || !h || h.latest.poId === l.prefillRef) return null;
  if (l.prefillDate && h.latest.date && dayjs(h.latest.date).isBefore(dayjs(l.prefillDate), 'day')) return null;
  return h.latest;
}

export { money as fmtPrice };
